import {
  applyPageUserEditsToClassificationsMap,
  applyPageUserEditsToDrawingRevisionGroupsMap,
  indexPageUserEditsByDocumentAndPage,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/applyUserEditsToSearchPageMetadata'
import { collectSearchDocumentUserEditEntryIds } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/collectSearchDocumentUserEditEntryIds'
import type {
  DrawingRevisionGroup,
  PageClassification,
  SearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { isAiDocumentSearchEngine } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import type {
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  parseDocumentMetadataUserEditEntries,
  parseDocumentPageMetadataUserEditEntries,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  documentMetadataUserEditsByEntryIds,
  documentPageMetadataUserEditsBySite,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'

interface UseSearchUserEditHydrationArgs {
  pageEditsEnabled: boolean
  documentEditsEnabled: boolean
  searchEngine: SearchEngine
  siteId: string
  bqDocumentIds: readonly string[]
  pageClassificationsMap: Map<string, PageClassification[]>
  drawingRevisionGroupsMap: Map<string, DrawingRevisionGroup[]>
}

interface UseSearchUserEditHydrationResult {
  pageClassificationsMap: Map<string, PageClassification[]>
  drawingRevisionGroupsMap: Map<string, DrawingRevisionGroup[]>
  documentUserEditsByBqId: Map<string, DocumentMetadataUserEditValues>
}

function indexDocumentUserEditsByBqId(
  edits: ReturnType<typeof parseDocumentMetadataUserEditEntries>
): Map<string, DocumentMetadataUserEditValues> {
  const map = new Map<string, DocumentMetadataUserEditValues>()
  for (const edit of edits) {
    const bqDocumentId = edit.entry.data.document_id
    if (!bqDocumentId) continue
    map.set(bqDocumentId, edit.entry.data.values)
  }
  return map
}

export function useSearchUserEditHydration({
  pageEditsEnabled,
  documentEditsEnabled,
  searchEngine,
  siteId,
  bqDocumentIds,
  pageClassificationsMap,
  drawingRevisionGroupsMap,
}: UseSearchUserEditHydrationArgs): UseSearchUserEditHydrationResult {
  const shouldLoadPageUserEdits =
    pageEditsEnabled && isAiDocumentSearchEngine(searchEngine)
  const shouldLoadDocumentUserEdits = documentEditsEnabled && Boolean(siteId)

  const pageUserEditLoadOptions = useMemo(() => {
    if (!shouldLoadPageUserEdits || !siteId) {
      return undefined
    }
    return documentPageMetadataUserEditsBySite({ site: siteId })
  }, [shouldLoadPageUserEdits, siteId])

  const documentUserEditLoadOptions = useMemo(() => {
    if (!shouldLoadDocumentUserEdits) {
      return undefined
    }
    const entryIds = collectSearchDocumentUserEditEntryIds(
      bqDocumentIds,
      siteId
    )
    return documentMetadataUserEditsByEntryIds(entryIds, { site: siteId })
  }, [shouldLoadDocumentUserEdits, bqDocumentIds, siteId])

  const { data: rawPageUserEdits } = useGetRolodexEntriesByLoadOptions<unknown>(
    pageUserEditLoadOptions
  )
  const { data: rawDocumentUserEdits } =
    useGetRolodexEntriesByLoadOptions<unknown>(documentUserEditLoadOptions)

  const pageUserEditsByDocAndPage = useMemo(() => {
    if (rawPageUserEdits === undefined) {
      return new Map<string, DocumentPageMetadataUserEditValues>()
    }
    return indexPageUserEditsByDocumentAndPage(
      parseDocumentPageMetadataUserEditEntries(rawPageUserEdits)
    )
  }, [rawPageUserEdits])

  const documentUserEditsByBqId = useMemo(() => {
    if (!shouldLoadDocumentUserEdits || rawDocumentUserEdits === undefined) {
      return new Map<string, DocumentMetadataUserEditValues>()
    }
    return indexDocumentUserEditsByBqId(
      parseDocumentMetadataUserEditEntries(rawDocumentUserEdits)
    )
  }, [shouldLoadDocumentUserEdits, rawDocumentUserEdits])

  const hydratedPageClassificationsMap = useMemo(() => {
    if (!shouldLoadPageUserEdits || pageUserEditsByDocAndPage.size === 0) {
      return pageClassificationsMap
    }
    return applyPageUserEditsToClassificationsMap(
      pageClassificationsMap,
      pageUserEditsByDocAndPage
    )
  }, [
    shouldLoadPageUserEdits,
    pageClassificationsMap,
    pageUserEditsByDocAndPage,
  ])

  const hydratedDrawingRevisionGroupsMap = useMemo(() => {
    if (!shouldLoadPageUserEdits || pageUserEditsByDocAndPage.size === 0) {
      return drawingRevisionGroupsMap
    }
    return applyPageUserEditsToDrawingRevisionGroupsMap(
      drawingRevisionGroupsMap,
      pageUserEditsByDocAndPage
    )
  }, [
    shouldLoadPageUserEdits,
    drawingRevisionGroupsMap,
    pageUserEditsByDocAndPage,
  ])

  return {
    pageClassificationsMap: hydratedPageClassificationsMap,
    drawingRevisionGroupsMap: hydratedDrawingRevisionGroupsMap,
    documentUserEditsByBqId,
  }
}
